import React, { useEffect, useState } from 'react';
import '../styles/ContenidoPremium.css';
import { API_URL } from '../config';

// Importación de imágenes
import blanco from '../images/blanco.png';
import celeste from '../images/celeste.png';
import negro from '../images/negro.png';
import rosado from '../images/rosado.png';
import ImagenDetalle from '../images/ImagenDetalle.jpg';
import fotoRustica from '../images/FondoRustico.png';
import diferencia from '../images/diferencia.jpg';

// Video referenciado desde /public para evitar procesamiento de webpack
const videoProducto = '/IMG_1579.mp4';

type StockData = Record<string, number>;

const SKU = 'PREM-SHAKER-650';
const PRODUCTS_URL = `${API_URL}/api/products`;
const COLORES = ['Blanco', 'Celeste', 'Negro', 'Rosado'] as const;
type Color = (typeof COLORES)[number];

const GALERIA_POR_COLOR: Record<Color, string[]> = {
  Blanco: [blanco, videoProducto, ImagenDetalle, diferencia, fotoRustica],
  Celeste: [celeste, videoProducto, ImagenDetalle, diferencia, fotoRustica],
  Negro: [negro, videoProducto, ImagenDetalle, diferencia, fotoRustica],
  Rosado: [rosado, videoProducto, ImagenDetalle, diferencia, fotoRustica],
};

const NOMBRE_PRODUCTO = 'Premium Shaker Electric 650 ML';
const PRECIO_BASE = 35294;
const PRECIO_DESCUENTO = 30000;

const ContenidoPremium: React.FC = () => {
  const [colorActual, setColorActual] = useState<Color>('Negro');
  const [fotoIndex, setFotoIndex] = useState<number>(0);
  const [stock, setStock] = useState<StockData>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [viewers] = useState<number>(() => 12 + Math.floor(Math.random() * 10));

  const recursoPrincipal = GALERIA_POR_COLOR[colorActual][fotoIndex];

  // Función mejorada para detectar video (insensible a mayúsculas/minúsculas)
  const esVideo = (url: string) => {
    if (!url) return false;
    return url.toLowerCase().includes('.mp4');
  };

  const formatCLP = (n: number) =>
    n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${PRODUCTS_URL}/${SKU}/availability`, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`Fallo stock`);
        const data = await res.json();
        setStock(data?.stock || {});
      } catch (err) {
        console.error('Error stock', err);
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  const sinStock = (color: string) => (stock[color] ?? 0) <= 0;
  const sinStockActual = sinStock(colorActual);
  const bajoStock = (stock[colorActual] ?? 0) > 0 && (stock[colorActual] ?? 0) <= 5;

  const agregarAlCarrito = () => {
    if (sinStockActual) return;
    const nuevoItem = {
      nombre: NOMBRE_PRODUCTO,
      sku: SKU,
      imagen: GALERIA_POR_COLOR[colorActual][0],
      color: colorActual,
      precio: PRECIO_DESCUENTO,
      cantidad: 1,
    };
    const carrito: any[] = JSON.parse(localStorage.getItem('carrito') || '[]');
    const idx = carrito.findIndex(it => it.sku === SKU && it.color === colorActual);

    if (idx >= 0) {
      if (carrito[idx].cantidad + 1 > (stock[colorActual] || 0)) {
        alert("Sin stock suficiente");
        return;
      }
      carrito[idx].cantidad += 1;
    } else {
      carrito.push(nuevoItem);
    }

    localStorage.setItem('carrito', JSON.stringify(carrito));
    (window as any).actualizarCantidadCarrito?.();
    (window as any).abrirCarritoDropdown?.();
    window.dispatchEvent(new Event('carrito:change'));
  };

  return (
    <div className="cp-master-container">
      <div className="cp-trust-full-width">
        <div className="cp-trust-limit">
          <div className="trust-item">⚡ ENVÍO EXPRESS</div>
          <div className="trust-divider">|</div>
          <div className="trust-item">🛡️ GARANTÍA 60 DÍAS</div>
          <div className="trust-divider">|</div>
          <div className="trust-item">💳 3 CUOTAS SIN INTERÉS</div>
        </div>
      </div>

      <section className="cp-content-wrapper">
        <div className="cp-main-grid">
          {/* LADO IZQUIERDO: Visuales con Galería */}
          <div className="cp-visual-column">
            <div className="cp-gallery-container">
              <div className="cp-thumbnails">
                {GALERIA_POR_COLOR[colorActual].map((url, idx) => (
                  <div
                    key={idx}
                    className={`cp-thumb-item ${fotoIndex === idx ? 'active' : ''} ${esVideo(url) ? 'is-video-thumb' : ''}`}
                    onMouseEnter={() => setFotoIndex(idx)}
                  >
                    {esVideo(url) ? (
                      <>
                        <video
                          src={url}
                          muted
                          playsInline
                          preload="metadata"
                          className="thumb-video-preview"
                          onLoadedMetadata={(e) => { (e.target as HTMLVideoElement).currentTime = 0.5; }}
                        />
                        <div className="thumb-video-overlay">▶</div>
                      </>
                    ) : (
                      <img src={url} alt={`Miniatura ${idx}`} />
                    )}
                  </div>
                ))}
              </div>

              <div className="cp-image-card">
                {esVideo(recursoPrincipal) ? (
                  <video
                    key={recursoPrincipal} // Key importante para resetear el reproductor al cambiar
                    src={recursoPrincipal}
                    className="cp-main-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img src={recursoPrincipal} alt={NOMBRE_PRODUCTO} className="cp-main-image" />
                )}

                {bajoStock && (
                  <div className="cp-stock-badge">
                    🔥 ¡ÚLTIMAS {stock[colorActual]} UNIDADES!
                  </div>
                )}
              </div>
            </div>

            <div className="cp-live-counter">
              <span className="live-dot"></span>
              <strong>{viewers} personas</strong> viendo este producto ahora
            </div>
          </div>

          {/* LADO DERECHO: Info */}
          <aside className="cp-info-column">
            <header className="cp-header">
              <h1 className="cp-title">{NOMBRE_PRODUCTO}</h1>
              <p className="cp-tagline">Mezcla perfecta, diseño de alto rendimiento.</p>
            </header>

            <hr className="cp-divider" />

            <div className="cp-price-section">
              <div className="cp-price-row">
                <span className="price-now">{formatCLP(PRECIO_DESCUENTO)}</span>
                <span className="price-before">{formatCLP(PRECIO_BASE)}</span>
                <span className="price-tag">-15% HOY</span>
              </div>
              <p className="cp-quotas">Hasta 3 cuotas de {formatCLP(10000)} sin interés 💳</p>
            </div>

            <div className="cp-coupon-highlight">
              <span className="coupon-icon">🎟️</span>
              <p>Usa el código <strong>PRIMERCMP</strong> para un descuento extra de <strong>$5.000</strong></p>
            </div>

            <hr className="cp-divider" />

            <div className="cp-selector-section">
              <label className="cp-label">Selecciona tu color: <strong>{colorActual}</strong></label>
              <div className="cp-swatch-grid">
                {COLORES.map((c) => (
                  <button
                    key={c}
                    className={`cp-swatch-pill ${colorActual === c ? 'active' : ''} ${sinStock(c) ? 'disabled' : ''}`}
                    onClick={() => {
                      if (!sinStock(c)) {
                        setColorActual(c);
                        setFotoIndex(0);
                      }
                    }}
                    disabled={sinStock(c)}
                  >
                    <span className={`dot-color ${c.toLowerCase()}`} />
                    <span className="swatch-name">{c}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="cp-features">
              <ul className="cp-feature-list">
                <li>✅ Mezcla homogénea en segundos (sin grumos)</li>
                <li>✅ Materiales premium, libre de BPA</li>
                <li>✅ Carga USB-C y batería de larga duración</li>
              </ul>
            </div>

            <div className="cp-action-area">
              {!loading && (
                <div className={`cp-stock-notice ${sinStockActual ? 'out' : 'ok'}`}>
                  {sinStockActual ? '❌ Sin stock para este color' : '✅ Disponible para entrega inmediata'}
                </div>
              )}
              <button
                className={`cp-cta-btn ${sinStockActual ? 'disabled' : 'active'}`}
                onClick={agregarAlCarrito}
                disabled={loading || sinStockActual}
              >
                {sinStockActual ? 'SIN STOCK' : 'AGREGAR AL CARRITO'}
              </button>
            </div>

          </aside>
        </div>
      </section>
    </div>
  );
};

export default ContenidoPremium;